/* platform/_ui — small presentational primitives shared by Platform + Org tiers.
   Extracted/generalized from the prototype's Control Center internals. Clean ESM. */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';

const C_MAP = { blue: 'var(--nx-accent, #125AB0)', green: 'var(--nx-success, #0E8C5A)', amber: 'var(--nx-warning, #E08A00)', red: 'var(--nx-danger, #DC2626)', purple: 'var(--nx-accent-2, #6E3FBA)', gray: 'var(--nx-text-muted, #64748B)', slate: 'var(--nx-text-faint, #475569)' };
const rc = (c) => C_MAP[c] || c || '#125AB0';

export const StatCard = ({ label, value, sub, color, icon, onClick }) => (
  <div
    className={'nx-cc-stat' + (onClick ? ' is-clickable' : '')}
    onClick={onClick}
    style={color ? { '--nx-cc-accent': rc(color) } : {}}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e => (e.key === 'Enter' || e.key === ' ') && onClick()) : undefined}
    aria-label={onClick ? `${label}: ${value}` : undefined}
  >
    {icon && <span className="nx-cc-stat-icon" style={{ background: 'color-mix(in srgb, var(--nx-cc-accent, #125AB0) 9%, transparent)', color: 'var(--nx-cc-accent, #125AB0)' }}><NexusIcon name={icon} size={16} /></span>}
    <span className="nx-cc-stat-label">{label}</span>
    <span className="nx-cc-stat-value">{value}</span>
    {sub && <span className="nx-muted nx-mini">{sub}</span>}
  </div>
);

export const TabBar = ({ tabs, active, onChange }) => (
  <div className="nx-cc-tabs" role="tablist">
    {tabs.map(t => (
      <button
        key={t.id}
        role="tab"
        aria-selected={active === t.id}
        aria-controls={'nx-tab-panel-' + t.id}
        className={'nx-cc-tab' + (active === t.id ? ' is-active' : '')}
        onClick={() => onChange(t.id)}
      >
        {t.icon && <NexusIcon name={t.icon} size={13} />}{t.label}
      </button>
    ))}
  </div>
);

export const PageHead = ({ title, sub, actions, back }) => (
  <div className="nx-cc-page-head">
    {back && (
      <button type="button" className="nx-back-nav" onClick={back.onClick}>
        <NexusIcon name="ChevronLeft" size={13} />
        {back.label}
      </button>
    )}
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, width: '100%' }}>
      <div>
        <h2 className="nx-cc-page-title">{title}</h2>
        {sub && <span className="nx-cc-page-sub">{sub}</span>}
      </div>
      {actions && <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>{actions}</div>}
    </div>
  </div>
);

/* ── Analytics tooltip — portal-based so overflow:auto containers never clip it ── */
export function AnalyticsTooltip({ visible, x, y, title, label, value, pct, color }) {
  if (!visible || x == null || y == null) return null;
  return createPortal(
    <div role="tooltip" className="nx-analytics-tooltip" style={{ left: x, top: y }}>
      {title && <div className="nx-at-title">{title}</div>}
      {label && <div className="nx-at-label">{label}</div>}
      <div className="nx-at-row">
        <span className="nx-at-value" style={{ color }}>
          {typeof value === 'number' ? value.toLocaleString() : (value ?? 0)}
        </span>
        {pct != null && <span className="nx-at-pct">{pct}%</span>}
      </div>
    </div>,
    document.body
  );
}

/* ── Scalable bar chart — bars fill the available card height via flex ── */
export const MiniBar = ({ points, color, label, current, labels, chartTitle }) => {
  const [tip, setTip] = useState(null);
  const safe = (points || []).map(v => (v == null || isNaN(Number(v))) ? 0 : Number(v));
  const max  = Math.max(...safe, 1);
  const sum  = safe.reduce((s, v) => s + v, 0);
  const ariaLabel = (chartTitle ? chartTitle + ' — ' : '') +
    safe.map((v, i) => `${labels?.[i] ?? (i + 1)}: ${v}`).join(', ');

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, width: '100%' }}>
      {/* Chart area — fills all remaining space; bars grow proportionally */}
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {/* Horizontal guide lines at 25 / 50 / 75 % from baseline */}
        {[25, 50, 75].map(pct => (
          <div key={pct} aria-hidden="true" style={{
            position: 'absolute', bottom: pct + '%', left: 0, right: 0,
            height: 1, background: 'var(--nx-border, #E2E8F0)', pointerEvents: 'none',
          }} />
        ))}
        {/* Baseline */}
        <div aria-hidden="true" style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          height: 2, background: 'color-mix(in srgb, ' + color + ' 20%, transparent)', borderRadius: 1, pointerEvents: 'none',
        }} />
        {/* Bars — absolutely fill chart area, grow from bottom */}
        <div
          role="img"
          aria-label={ariaLabel}
          onMouseLeave={() => setTip(null)}
          style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 2,
            display: 'flex', alignItems: 'flex-end', gap: 3,
          }}
        >
          {safe.map((v, i) => {
            const isCurrent = i === safe.length - 1;
            return (
              <div
                key={i}
                onMouseEnter={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  setTip({ i, x: Math.max(75, Math.min(window.innerWidth - 75, r.left + r.width / 2)), y: r.top });
                }}
                style={{
                  flex: 1,
                  background: isCurrent ? color : color + '80',
                  borderRadius: '3px 3px 0 0',
                  height: v > 0 ? (v / max * 100) + '%' : '2px',
                  minHeight: v > 0 ? 4 : 2,
                  transition: 'height 0.4s ease, opacity 0.12s',
                  cursor: 'crosshair',
                  opacity: tip !== null && tip.i !== i ? 0.55 : 1,
                }}
              />
            );
          })}
        </div>
      </div>
      {/* Chart sub-label: period range + aggregate count */}
      <div style={{
        flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginTop: 8,
      }}>
        <span style={{ fontSize: 11, color: 'var(--nx-text-muted, #64748B)' }}>{label}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{current}</span>
      </div>
      <AnalyticsTooltip
        visible={tip !== null}
        x={tip?.x}
        y={tip?.y}
        title={chartTitle}
        label={labels?.[tip?.i]}
        value={tip !== null ? safe[tip.i] : 0}
        pct={sum > 0 && tip !== null ? Math.round(safe[tip.i] / sum * 100) : null}
        color={color}
      />
    </div>
  );
};

/* Accessible toggle switch (M-02 / M-03 / M-24) */
export const Toggle = ({ checked, onChange, disabled, 'aria-labelledby': labelledBy, 'aria-label': ariaLabel }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-labelledby={labelledBy}
    aria-label={labelledBy ? undefined : ariaLabel}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    style={{
      width: 38, height: 22, borderRadius: 11, border: 'none',
      background: checked ? 'var(--nx-accent)' : 'var(--nx-border)',
      cursor: disabled ? 'default' : 'pointer', position: 'relative', flexShrink: 0,
      transition: 'background 0.2s', opacity: disabled ? 0.6 : 1,
    }}
  >
    <span style={{
      position: 'absolute', top: 3, left: checked ? 19 : 3, width: 16, height: 16,
      borderRadius: '50%', background: 'var(--nx-surface)', transition: 'left 0.2s',
      boxShadow: '0 1px 3px var(--nx-border)',
    }} />
  </button>
);

/* Accessible progress bar (M-08) */
export const ProgressBar = ({ value, max = 100, color, height = 6, label }) => {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  const r   = Math.ceil(height / 2);
  return (
    <div
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-label={label}
      style={{ height, background: 'var(--nx-border)', borderRadius: r, overflow: 'hidden' }}
    >
      <div style={{ height: '100%', width: pct + '%', background: color || 'var(--nx-accent)', borderRadius: r, transition: 'width 0.4s ease' }} />
    </div>
  );
};

/* Circular score ring SVG (M-07) — renders the ring + score overlay only, no card wrapper */
export const ScoreRing = ({ score, color, size = 110 }) => {
  const r    = Math.round(size * 46 / 110);
  const sw   = Math.round(size * 8 / 110);
  const fs   = Math.round(size * 28 / 110);
  const cx   = size / 2;
  const circ = 2 * Math.PI * r;
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }} aria-hidden="true">
        <circle cx={cx} cy={cx} r={r} fill="none" stroke="var(--nx-border)" strokeWidth={sw} />
        <circle cx={cx} cy={cx} r={r} fill="none" stroke={color} strokeWidth={sw}
          strokeDasharray={circ}
          strokeDashoffset={circ * (1 - score / 100)}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: fs, fontWeight: 800, color, lineHeight: 1 }}>{score}</div>
        <div style={{ fontSize: 10, color: 'var(--nx-text-muted)', marginTop: 2 }}>/ 100</div>
      </div>
    </div>
  );
};

/* Lightweight table header cell (mirrors prototype Th) */
export const Th = ({ icon, children, end }) => (
  end ? <th className="nx-th-end" /> :
  <th><span className="nx-th-inner">{icon && <NexusIcon name={icon} size={12} />}<span>{children}</span></span></th>
);

/* Modal shell reused from the prototype confirm style.
   scrollable=true: flex-column, body scrolls, footer sticks to bottom.
   dialog=true: enterprise form dialog — properly centered, max-height 90vh,
     fixed header with icon/title/subtitle and X close button, scrollable body,
     sticky footer, body-scroll lock, ESC to close. */
export function TenancyModal({ title, subtitle, icon, children, footer, onClose, danger, width = 500, scrollable, dialog }) {
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  useEffect(() => {
    if (!dialog) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onCloseRef.current(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [dialog]);

  if (dialog) {
    const accentBg   = danger ? 'rgba(220,38,38,0.10)' : 'var(--nx-accent-tint,rgba(18,90,176,0.08))';
    const accentClr  = danger ? 'var(--nx-danger,#DC2626)' : 'var(--nx-accent,#125AB0)';
    return (
      <div
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(15,42,74,0.40)',
          backdropFilter: 'blur(4px)',
          zIndex: 400,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '16px',
          animation: 'nx-veil-in 0.2s ease',
        }}
        onClick={onClose}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="nx-tm-title-d"
          onClick={e => e.stopPropagation()}
          style={{
            display: 'flex', flexDirection: 'column',
            width: `min(${width}px, 95vw)`,
            maxHeight: '90vh',
            background: 'var(--nx-surface)',
            borderRadius: 14,
            boxShadow: '0 24px 64px rgba(15,23,42,0.22), 0 8px 20px rgba(15,23,42,0.10)',
            border: '1px solid var(--nx-border,#E2E8F0)',
            overflow: 'hidden',
            animation: 'nx-dialog-in 0.2s cubic-bezier(0.32,0.72,0,1)',
          }}
        >
          {/* ── Fixed header ── */}
          <div style={{
            flexShrink: 0,
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '14px 20px',
            borderBottom: '1px solid var(--nx-border,#E2E8F0)',
            background: 'var(--nx-surface)',
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: 8, flexShrink: 0,
              background: accentBg, color: accentClr,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <NexusIcon name={icon || 'Settings'} size={18} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div id="nx-tm-title-d" style={{ fontWeight: 700, fontSize: 15, color: 'var(--nx-text)', lineHeight: 1.3 }}>{title}</div>
              {subtitle && (
                <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {subtitle}
                </div>
              )}
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              style={{
                flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer',
                padding: 6, borderRadius: 6, color: 'var(--nx-text-muted)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background 0.12s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--nx-surface-alt)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
            >
              <NexusIcon name="X" size={16} />
            </button>
          </div>

          {/* ── Scrollable body ── */}
          <div style={{
            flex: 1, minHeight: 0,
            overflowY: 'auto', overflowX: 'hidden',
            padding: '20px 24px',
          }}>
            {children}
          </div>

          {/* ── Sticky footer ── */}
          {footer && (
            <div style={{
              flexShrink: 0,
              padding: '12px 20px',
              borderTop: '1px solid var(--nx-border,#E2E8F0)',
              background: 'var(--nx-surface-alt)',
              display: 'flex', justifyContent: 'flex-end', gap: 8,
              flexWrap: 'wrap',
            }}>
              {footer}
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ── Legacy non-dialog mode (all existing callers unchanged) ── */
  return (
    <div className="nx-cmd-veil" style={{ alignItems: 'center', zIndex: 200 }} onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="nx-tm-title"
        className="nx-confirm"
        onClick={e => e.stopPropagation()}
        style={scrollable
          ? { width, maxWidth: 'calc(100vw - 32px)', display: 'flex', flexDirection: 'column', maxHeight: '88vh', padding: 0 }
          : { width, maxWidth: 'calc(100vw - 32px)' }
        }
      >
        <div style={scrollable ? { padding: '20px 24px 0', textAlign: 'center', flexShrink: 0 } : {}}>
          <div className={'nx-confirm-icon' + (danger ? ' is-danger' : '')}><NexusIcon name={icon || 'Settings'} size={18} /></div>
          <h3 id="nx-tm-title" className="nx-confirm-title" style={scrollable ? { marginBottom: 16 } : {}}>{title}</h3>
        </div>
        {scrollable ? (
          <>
            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '0 24px 8px', minHeight: 0, textAlign: 'left' }}>
              {children}
            </div>
            {footer && (
              <div style={{ padding: '12px 20px', borderTop: '1px solid var(--nx-border)', background: 'var(--nx-surface-alt)', flexShrink: 0, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                {footer}
              </div>
            )}
          </>
        ) : children}
      </div>
    </div>
  );
}
